import { useState, useEffect } from "react";
import DataTable from '../DataTable/DataTable';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { DateTime } from "luxon";
import "../../pages/jobdesccandidatelist/jdcandidatelist.css";
import {
  NotificationManager,
} from "react-notifications";
import getCandidateByJd from "../../apis/candidatescreening/getcandidatebyjdid";
import interviewvetcandidate from "../../apis/vettest/interviewvetcandidate";
import Button from '@mui/material/Button';
import { useNavigate } from "react-router-dom";

const stylesSX = {
  odd: {
    background: '#F6F6F6 !important',
  },
  even: {
    background: '#ffffff !important',
  },
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
  waitingText: {
    marginLeft: '5px',
  },
};

const columnTimer = 400;

const JdCandidateTable = (props) => {
  const {
    userRecruitmentpartnerId,
    triggerAction,
    resetTrigger,
    filterValues,
    isfilterSearch,
    setIsfilterSearch,
    jdId,
    toggleAiScreen,
    vetTestId,
  } = props;

  const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalResult, setTotalResult] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isSearching, setIsSearching] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [columnFilters, setColumnFilters] = useState({});
  const location = useNavigate();

  // Define columns for the table
  const columns = [
    {
      accessorKey: "action",
      header: "Action",
      width: "150px",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Button
          variant="contained"
          className="viewanalysisbutton_jdCandidateList"
          onClick={() => {
            const candidateId = row.original?._id || row.original?.id;
            location(
              `/candidate-interview-analysis/${candidateId}?int=${toggleAiScreen ? "ai" : "vet"}`,
              !toggleAiScreen ?
                { state: { vetTestId: vetTestId, jdId: jdId } }
                : undefined
            )
          }}
        >
          Analysis
        </Button>
      ),
    },
    {
      id: "name",
      header: "Candidate Name",
      accessorFn: (row) =>
        row.name ||
        `${row.firstName || ""} ${row.lastName || ""}`.trim(),
      width: "180px",
      enableSorting: true,
      enableFiltering: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: "email",
      header: "Candidate Email",
      width: "200px",
      enableSorting: true,
      enableFiltering: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: "isVerified",
      header: "Verified",
      width: "100px",
      enableSorting: true,
      enableFiltering: true,
      enableColumnFilter: true,
      cell: ({ getValue }) => {
        if (!toggleAiScreen) return "Yes";
        return getValue() ? "Yes" : "No";
      }
    },
    {
      accessorKey: "createdAt",
      header: "Created Date",
      width: "150px",
      enableSorting: false,
      enableFiltering: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const date = row.original.createdAt;
        return date ? DateTime.fromISO(date).toFormat("MM/dd/yyyy") : "";
      },
    },
  ];


  const fetchData = async () => {
    if (!jdId) {
      NotificationManager.error("Job Description ID is required", "Error", 5000);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const offset = (pageNumber - 1) * takeNumberOfResult;

      const payload = {
        jdId: jdId,
        recruitmentpartnerId: userRecruitmentpartnerId,
        offset: offset,
        limit: takeNumberOfResult,
        filters: {
          candidateName: columnFilters.name || filterValues.candidateName || '',
          candidateEmail: columnFilters.email || filterValues.candidateEmail || '',
        },
      };
      const vetCandidatePayload = {
        testId: vetTestId,
        recruitmentpartnerId: userRecruitmentpartnerId,
        offset: offset,
        limit: takeNumberOfResult,
        filters: {
          candidateName: columnFilters.name || filterValues.candidateName || '',
          candidateEmail: columnFilters.email || filterValues.candidateEmail || '',
        },
      };

      const response = toggleAiScreen
        ? await getCandidateByJd(payload)
        : await interviewvetcandidate(vetCandidatePayload);

      if (response.data && response.data.data) {
        const candidates = toggleAiScreen ? response.data.data.candidates || [] : response.data.data || [];
        const totalCount = toggleAiScreen ? response.data.data.totalCount || [] : response.data.totalCount || [];

        const currentPageData = candidates.slice(
          (pageNumber - 1) * takeNumberOfResult,
          pageNumber * takeNumberOfResult
        );

        setTableData(candidates);
        setTotalResult(totalCount);
      } else {
        setTableData([]);
        setTotalResult(0);
      }
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message || "Error fetching candidate data",
        "Error",
        5000
      );
      setTableData([]);
      setTotalResult(0);
    } finally {
      setIsSearching(false);
      setIsfilterSearch(false);
    }
  };


  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      NotificationManager.success("Export functionality to be implemented", "Info", 3000);
    } catch (error) {
      NotificationManager.error("Error exporting data", "Error", 5000);
    } finally {
      setIsExporting(false);
      resetTrigger();
    }
  };


  useEffect(() => {
    if (triggerAction) {
      exportToExcel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAction]);


  useEffect(() => {
    if (isfilterSearch) {
      setPageNumber(1);
      setFetchTrigger(prev => prev + 1);
      // Reset the filter search flag after processing
      setTimeout(() => setIsfilterSearch(false), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isfilterSearch]);


  // Handle column filter changes
  const handleColumnFilterChange = (filters) => {
    const filterObj = {};
    filters.forEach(filter => {
      filterObj[filter.id] = filter.value;
    });
    setColumnFilters(filterObj);
    setPageNumber(1);
  };

  useEffect(() => {
    // Skip the debounce on initial mount
    if (fetchTrigger === 0) {
      return;
    }

    const timer = setTimeout(() => {
      setFetchTrigger(prev => prev + 1);
    }, columnTimer);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger, pageNumber, takeNumberOfResult, columnFilters]);

  return (
    <>
      <Backdrop sx={stylesSX.backdrop} open={isExporting}>
        <CircularProgress color="inherit" />
        <Typography sx={stylesSX.waitingText}>Please Wait...</Typography>
      </Backdrop>

      <DataTable
        columns={columns}
        data={tableData}
        totalRecords={totalResult}
        isLoading={isSearching}
        isServerSide={true}
        onPageChange={(page) => {
          setPageNumber(page);
          setFetchTrigger(prev => prev + 1);
        }}
        onPageSizeChange={(size) => {
          setTakeNumberOfResult(size);
          setPageNumber(1);
          setFetchTrigger(prev => prev + 1);
        }}
        onFilterChange={handleColumnFilterChange}
        currentPage={pageNumber}
        pageSize={takeNumberOfResult}
        pageSizeOptions={[10, 20, 50]}
      />
    </>
  );
};

export default JdCandidateTable;
